export const KNOWN_ALLERGY_IDS = [
    'peanuts',
    'treeNuts',
    'milk',
    'eggs',
    'wheat',
    'gluten',
    'soy',
    'fish',
    'shellfish',
    'sesame',
    'mustard',
    'celery',
    'sulphites',
    'lupin',
    'molluscs',
    'favaBeans',
] as const;

export type KnownAllergyId = (typeof KNOWN_ALLERGY_IDS)[number];

export const ALLERGY_CONFIG: { id: KnownAllergyId; labelKey: string; emoji: string }[] = [
    { id: 'peanuts', labelKey: 'allergyPeanuts', emoji: '🥜' },
    { id: 'treeNuts', labelKey: 'allergyTreeNuts', emoji: '🌰' },
    { id: 'milk', labelKey: 'allergyMilk', emoji: '🥛' },
    { id: 'eggs', labelKey: 'allergyEggs', emoji: '🥚' },
    { id: 'wheat', labelKey: 'allergyWheat', emoji: '🌾' },
    { id: 'gluten', labelKey: 'allergyGluten', emoji: '🍞' },
    { id: 'soy', labelKey: 'allergySoy', emoji: '🫘' },
    { id: 'fish', labelKey: 'allergyFish', emoji: '🐟' },
    { id: 'shellfish', labelKey: 'allergyShellfish', emoji: '🦐' },
    { id: 'sesame', labelKey: 'allergySesame', emoji: '🫘' },
    { id: 'mustard', labelKey: 'allergyMustard', emoji: '🟡' },
    { id: 'celery', labelKey: 'allergyCelery', emoji: '🥬' },
    { id: 'sulphites', labelKey: 'allergySulphites', emoji: '🍷' },
    { id: 'lupin', labelKey: 'allergyLupin', emoji: '🌼' },
    { id: 'molluscs', labelKey: 'allergyMolluscs', emoji: '🦑' },
    { id: 'favaBeans', labelKey: 'allergyFavaBeans', emoji: '🫛' },
];

/** Most common allergens — shown as quick-pick chips before any selection. */
export const TOP_ALLERGY_IDS: KnownAllergyId[] = [
    'peanuts',
    'milk',
    'eggs',
    'treeNuts',
    'gluten',
];

export function getAllergyConfig(id: KnownAllergyId) {
    return ALLERGY_CONFIG.find((entry) => entry.id === id);
}

const ALLERGY_ID_SET = new Set<string>(KNOWN_ALLERGY_IDS);

export function isKnownAllergyId(value: string): value is KnownAllergyId {
    return ALLERGY_ID_SET.has(value);
}

export function normalizeKnownAllergies(value: unknown): KnownAllergyId[] {
    if (!Array.isArray(value)) return [];
    const seen = new Set<KnownAllergyId>();
    for (const entry of value) {
        if (typeof entry !== 'string') continue;
        const trimmed = entry.trim();
        if (isKnownAllergyId(trimmed)) {
            seen.add(trimmed);
        }
    }
    return KNOWN_ALLERGY_IDS.filter((id) => seen.has(id));
}

export function toggleKnownAllergy(
    current: readonly KnownAllergyId[],
    allergyId: KnownAllergyId,
): KnownAllergyId[] {
    const set = new Set(current);
    if (set.has(allergyId)) {
        set.delete(allergyId);
    } else {
        set.add(allergyId);
    }
    return KNOWN_ALLERGY_IDS.filter((id) => set.has(id));
}

/** Ingredient keyword hints per allergen (EN + HE). Used to infer allergens from recipe text. */
const ALLERGEN_INGREDIENT_KEYWORDS: Record<KnownAllergyId, readonly string[]> = {
    peanuts: ['peanut', 'peanuts', 'peanut butter', 'arachis', 'בוטן', 'חמאת בוטנים', 'בוטנים'],
    treeNuts: [
        'almond', 'almonds', 'walnut', 'walnuts', 'cashew', 'cashews', 'pistachio', 'pistachios',
        'hazelnut', 'hazelnuts', 'pecan', 'pecans', 'macadamia', 'brazil nut', 'pine nut', 'chestnut',
        'שקד', 'שקדים', 'אגוז', 'אגוזים', 'קשיו', 'פיסטוק', 'פקאן', 'לוז', 'ערמונים',
    ],
    milk: [
        'milk', 'cream', 'butter', 'cheese', 'yogurt', 'yoghurt', 'dairy', 'whey', 'casein', 'lactose',
        'ghee', 'ricotta', 'mozzarella', 'parmesan', 'cheddar', 'feta',
        'חלב', 'גבינ', 'חמאה', 'שמנת', 'יוגורט', 'חלבון חלב', 'לקטוז',
    ],
    eggs: ['egg', 'eggs', 'egg white', 'egg yolk', 'mayonnaise', 'mayo', 'meringue', 'ביצ', 'מאיונז'],
    wheat: ['wheat', 'flour', 'semolina', 'durum', 'bulgur', 'couscous', 'חיטה', 'קמח', 'סולת'],
    gluten: [
        'gluten', 'seitan', 'barley', 'rye', 'spelt', 'malt', 'oat', 'oats',
        'גלוטן', 'שעורה', 'שיפון', 'כוסמין', 'שיבולת שועל',
    ],
    soy: ['soy', 'soya', 'tofu', 'tempeh', 'edamame', 'miso', 'soy sauce', 'סויה', 'טופו', 'רוטב סויה'],
    fish: [
        'fish', 'salmon', 'tuna', 'cod', 'sardine', 'anchovy', 'anchovies', 'trout', 'mackerel', 'herring',
        'דג', 'דגים', 'סלמון', 'טונה', 'מושט', 'אנשובי', 'הרינג',
    ],
    shellfish: [
        'shrimp', 'prawn', 'prawns', 'crab', 'lobster', 'crayfish', 'crawfish',
        'שרימפ', 'סרטן', 'לובסטר', 'פירות ים',
    ],
    sesame: ['sesame', 'tahini', 'halva', 'halvah', 'שומשום', 'טחינה', 'חלבה'],
    mustard: ['mustard', 'חרדל'],
    celery: ['celery', 'celeriac', 'סלרי', 'שורש סלרי'],
    sulphites: ['sulphite', 'sulfite', 'sulphites', 'sulfites', 'sulfur dioxide', 'e220', 'e221', 'e222', 'e223', 'e224', 'e226', 'e227', 'e228', 'סולפיט', 'יין'],
    lupin: ['lupin', 'lupine', 'lupini', 'תורמוס'],
    molluscs: [
        'squid', 'octopus', 'mussel', 'mussels', 'clam', 'clams', 'oyster', 'oysters', 'scallop', 'scallops', 'calamari', 'snail', 'escargot',
        'דיונון', 'תמנון', 'צדפות', 'צדפה', 'האצים', 'חילזון', 'רכיכות',
    ],
    favaBeans: [
        'fava', 'fava bean', 'fava beans', 'broad bean', 'broad beans', 'ful medames', 'ful mudammas',
        'פול', 'פולים', 'פול מצרי', 'פול מרוקאי',
    ],
};

function normalizeIngredientText(value: string): string {
    return value.toLowerCase().trim();
}

function collectRecipeIngredientTexts(recipe: { ingredients: { name: string }[]; englishTranslation?: { ingredients?: { name: string }[] } | null }): string[] {
    const names: string[] = [];
    for (const ing of recipe.ingredients) {
        if (ing.name.trim()) names.push(normalizeIngredientText(ing.name));
    }
    const translated = recipe.englishTranslation?.ingredients;
    if (Array.isArray(translated)) {
        for (const ing of translated) {
            if (ing.name.trim()) names.push(normalizeIngredientText(ing.name));
        }
    }
    return names;
}

function ingredientMatchesKeyword(ingredient: string, keyword: string): boolean {
    const normalizedKeyword = normalizeIngredientText(keyword);
    if (!normalizedKeyword) return false;
    if (ingredient.includes(normalizedKeyword)) return true;
    if (normalizedKeyword.length >= 4 && ingredient.split(/\s+/).some((word) => word.startsWith(normalizedKeyword))) {
        return true;
    }
    return false;
}

export function ingredientTextsMatchAllergy(
    ingredientTexts: readonly string[],
    allergyId: KnownAllergyId,
): boolean {
    const keywords = ALLERGEN_INGREDIENT_KEYWORDS[allergyId];
    return ingredientTexts.some((ingredient) =>
        keywords.some((keyword) => ingredientMatchesKeyword(ingredient, keyword)),
    );
}

/** Detects likely allergens present in a recipe based on ingredient names. */
export function detectRecipeAllergens(recipe: {
    ingredients: { name: string }[];
    englishTranslation?: { ingredients?: { name: string }[] } | null;
    allergens?: KnownAllergyId[] | null;
}): KnownAllergyId[] {
    if (Array.isArray(recipe.allergens) && recipe.allergens.length > 0) {
        return normalizeKnownAllergies(recipe.allergens);
    }

    const ingredientTexts = collectRecipeIngredientTexts(recipe);
    if (ingredientTexts.length === 0) return [];

    const detected = new Set<KnownAllergyId>();
    for (const allergyId of KNOWN_ALLERGY_IDS) {
        const keywords = ALLERGEN_INGREDIENT_KEYWORDS[allergyId];
        const hasMatch = ingredientTexts.some((ingredient) =>
            keywords.some((keyword) => ingredientMatchesKeyword(ingredient, keyword)),
        );
        if (hasMatch) {
            detected.add(allergyId);
        }
    }

    return KNOWN_ALLERGY_IDS.filter((id) => detected.has(id));
}

/** True when the recipe should be hidden for the given allergy exclusions. */
export function recipeConflictsWithAllergyExclusions(
    recipe: Parameters<typeof detectRecipeAllergens>[0],
    excludeAllergens: readonly KnownAllergyId[],
): boolean {
    if (excludeAllergens.length === 0) return false;
    const present = detectRecipeAllergens(recipe);
    return excludeAllergens.some((allergyId) => present.includes(allergyId));
}
